import React, { FC } from 'react';
import { Card, CardContent, CardMedia, fade, Hidden, makeStyles, Theme, Typography } from '@material-ui/core';
import Link from 'next/link';
import { ArrowLineRightIcon } from 'svgs';

export interface TruckItemProps {
  image: string;
  title: string;
  text: string;
}

const useStyles = makeStyles((theme: Theme) => {
  return {
    card: {
      padding: 0,
      position: 'relative',
      minHeight: '100%',
      boxShadow: `0px 5px 20px ${fade(theme.palette.common.black, 0.15)}`,
      backgroundColor: theme.palette.common.white,
      [theme.breakpoints.up('md')]: {
        '&:hover': {
          '& $cardMedia': {
            transform: 'scale(1.1)',
          },
          '& $linkHelper': {
            right: 0,
            bottom: 0,
          },
        },
      },
    },
    linkHelper: {
      transition: 'right .25s, bottom .25s',
      position: 'absolute',
      width: 43,
      height: 43,
      backgroundColor: theme.palette.primary.main,
      right: -43,
      bottom: -43,
      textAlign: 'center',
      lineHeight: '43px',
    },
    cardContent: {
      '&:last-child': {
        padding: theme.spacing(2),
      },
      [theme.breakpoints.up('sm')]: {
        '&:last-child': {
          padding: theme.spacing(3, 4),
        },
      },
    },
    cardImage: {
      overflow: 'hidden',
      maxHeight: 105,
      [theme.breakpoints.up('sm')]: {
        maxHeight: 130,
      },
      [theme.breakpoints.up('md')]: {
        maxHeight: 200,
      },
      [theme.breakpoints.up('lg')]: {
        maxHeight: 283,
      },
    },
    cardMedia: {
      transform: 'scale(1)',
      transition: 'transform .5s ease-in-out',
      [theme.breakpoints.up('sm')]: {
        width: '102%',
      },
    },
    title: {
      textTransform: 'uppercase',
    },
    text: {
      fontSize: 10,
      maxWidth: 420,
      [theme.breakpoints.up('sm')]: {
        fontSize: 14,
      },
      [theme.breakpoints.up('md')]: {
        fontSize: 16,
        maxWidth: 390,
      },
      [theme.breakpoints.up('lg')]: {
        maxWidth: 420,
      },
    },
  };
});

const TruckItem: FC<TruckItemProps> = ({ image, text, title }) => {
  const classes = useStyles();
  const link = `/quote/#${title.split(' ').join('-')}`;

  return (
    <Link href={link}>
      <a>
        <Card className={classes.card}>
          <div className={classes.cardImage}>
            <CardMedia className={classes.cardMedia} component="img" alt={title} image={image} title={title} />
          </div>
          <CardContent className={classes.cardContent}>
            <Typography className={classes.title} color={'primary'} gutterBottom variant="h4" component={'h5'}>
              {title}
            </Typography>
            <Typography className={classes.text} variant="body2" color={'textPrimary'}>
              {text}
            </Typography>
          </CardContent>
          <Hidden smDown>
            <div className={classes.linkHelper}>
              <ArrowLineRightIcon />
            </div>
          </Hidden>
        </Card>
      </a>
    </Link>
  );
};

export default TruckItem;
